import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export interface CohortGatingResult {
  /** true when module is locked by cohort gating */
  isLocked: boolean;
  /** true while queries are in flight */
  isLoading: boolean;
}

/**
 * For a given course, returns a map of moduleId → whether it is locked
 * by cohort gating for the current user.
 *
 * Rules:
 * - No enrollment → not locked (content renders normally or enrollment gate handles it)
 * - Self-paced enrollment exists (cohort_id IS NULL) → not locked
 * - Cohort enrollment with gating_enforced = false → not locked
 * - Cohort enrollment with gating_enforced = true AND no self-paced enrollment →
 *   locked unless cohort_module_access row with unlocked_at IS NOT NULL exists
 */
export function useCohortModuleGating(courseId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["cohort-module-gating", courseId, user?.id],
    enabled: !!courseId && !!user,
    staleTime: 30_000,
    queryFn: async (): Promise<Record<string, boolean>> => {
      if (!user) return {};

      // 1. Get all enrollments for this course
      const { data: enrollments, error: enrollErr } = await supabase
        .from("course_enrollments")
        .select("id, cohort_id, gating_enforced")
        .eq("course_id", courseId)
        .eq("user_id", user.id);

      if (enrollErr) throw enrollErr;
      if (!enrollments || enrollments.length === 0) return {};

      // 2. If any self-paced enrollment exists → nothing locked
      const hasSelfPaced = enrollments.some((e) => e.cohort_id === null);
      if (hasSelfPaced) return {};

      // 3. Collect gated cohort ids
      const gatedCohortIds = enrollments
        .filter((e) => e.gating_enforced === true && e.cohort_id !== null)
        .map((e) => e.cohort_id as string);

      // If no gated enrollments (all open cohorts) → nothing locked
      if (gatedCohortIds.length === 0) return {};

      // 4. Get all modules for this course
      const { data: modules, error: modErr } = await supabase
        .from("modules")
        .select("id")
        .eq("course_id", courseId);

      if (modErr) throw modErr;
      if (!modules || modules.length === 0) return {};

      // 5. Get unlocked module access rows for these cohorts
      const { data: accessRows, error: accessErr } = await supabase
        .from("cohort_module_access")
        .select("module_id, unlocked_at")
        .in("cohort_id", gatedCohortIds)
        .not("unlocked_at", "is", null);

      if (accessErr) throw accessErr;

      const unlockedModuleIds = new Set(
        (accessRows || []).map((r) => r.module_id)
      );

      // 6. Build map: locked if not in unlocked set
      const result: Record<string, boolean> = {};
      for (const mod of modules) {
        result[mod.id] = !unlockedModuleIds.has(mod.id);
      }
      return result;
    },
  });
}

/**
 * Check if a single module is locked by cohort gating.
 */
export function useIsModuleCohortLocked(
  courseId: string,
  moduleId: string | null | undefined
): CohortGatingResult {
  const { data: gatingMap, isLoading } = useCohortModuleGating(courseId);

  if (!moduleId || !gatingMap) {
    return { isLocked: false, isLoading };
  }

  return {
    isLocked: gatingMap[moduleId] === true,
    isLoading,
  };
}
